// src/components/Bills.js — Bookkeeping → Bills: vendor bills, Pay Bills, bill payments, check printing
// [marker: bills v1]
// <Bills canEdit />                                   the tab
// <BillModal billId? vendorId? canEdit onClose onSaved />   enter / edit a bill (vendor invoice)
// <PayBillsModal canEdit preselect? onClose onPaid />  pick bills, choose check / transfer / credit card
// <BillPaymentModal paymentId canEdit onClose onSaved />   one payment: change, delete, print / reprint the check
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { AccountSelect } from './AccountSelect';
import { VendorModal } from './Vendors';
import { PrintChecksModal, CheckSettingsModal, printOneCheck } from './CheckPrint';
import { ClassSelect } from './ClassSelect'; // [marker: books-pro v1] classes, vendor credits, void

const r2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
const money = (n) => `${parseFloat(n || 0) < 0 ? '-' : ''}$${Math.abs(parseFloat(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const addDays = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const TERMS = ['Due on receipt', 'Net 10', 'Net 15', 'Net 30', 'Net 45', 'Net 60'];
const termDays = (t) => { if (!t) return null; if (/receipt/i.test(t)) return 0; const m = String(t).match(/(\d+)/); return m ? parseInt(m[1], 10) : null; };
const METHOD = { check: 'Check', ach: 'Online / bank transfer', credit_card: 'Credit card' };
const NOT_FOR_BILLS = ['accounts_payable', 'accounts_receivable'];
const statusOf = (b) => (b.status === 'void' ? 'Void' : b.is_credit ? (r2(b.balance) <= 0 ? 'Used' : 'Credit')
  : r2(b.balance) <= 0 ? 'Paid' : b.overdue ? 'Overdue' : r2(b.amount_paid) > 0 ? 'Partly paid' : 'Open');
const STATUS_COLOR = { Paid: 'var(--success)', Overdue: 'var(--danger)', 'Partly paid': '#b45309', Open: 'var(--gray-700)', Void: 'var(--gray-400)', Credit: '#7c3aed', Used: 'var(--gray-500)' };

function useVendors() {
  const [vendors, setVendors] = useState([]);
  const reload = useCallback(() => api.get('/vendors?active=all').then(r => setVendors(r.data || [])).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return [vendors, setVendors, reload];
}

// ─── The tab ──────────────────────────────────────────────────────────────────
export default function Bills({ canEdit }) {
  const [view, setView] = useState('bills'); // bills | payments
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sum, setSum] = useState(null);
  const [openBill, setOpenBill] = useState(null);      // id | 'new'
  const [openPayment, setOpenPayment] = useState(null);
  const [paying, setPaying] = useState(null);          // null | { preselect }
  const [printing, setPrinting] = useState(null);      // null | { preselect }
  const [setup, setSetup] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/ap/summary').then(r => setSum(r.data)).catch(() => {});
    const p = view === 'bills'
      ? api.get(`/ap/bills?status=${status}`).then(r => setRows(r.data || []))
      : api.get('/ap/payments').then(r => setPayments(r.data || []));
    p.catch(() => toast.error('Could not load.')).finally(() => setLoading(false));
  }, [view, status]);
  useEffect(() => { load(); }, [load]);

  const billCols = [
    { key: 'due_date', label: 'Due', width: 105, type: 'date', get: b => (b.due_date ? b.due_date + 'T00:00:00' : null),
      render: b => <span style={{ color: b.overdue ? 'var(--danger)' : undefined, fontWeight: b.overdue ? 700 : undefined }}>{b.due_date ? fmtDay(b.due_date) : '—'}</span> },
    { key: 'vendor_name', label: 'Vendor', width: 200, render: b => <strong>{b.vendor_name}</strong> },
    { key: 'is_credit', label: 'Type', width: 70, get: b => (b.is_credit ? 'Credit' : 'Bill') },
    { key: 'bill_number', label: 'Ref. No.', width: 110 },
    { key: 'bill_date', label: 'Bill Date', width: 105, type: 'date', get: b => b.bill_date + 'T00:00:00' },
    { key: 'notes', label: 'Memo', width: 200 },
    { key: 'accounts', label: 'Account', width: 180, defaultHidden: true },
    { key: 'total_amount', label: 'Amount', width: 110, type: 'money', align: 'right' },
    { key: 'amount_paid', label: 'Paid', width: 100, type: 'money', align: 'right', defaultHidden: status === 'open' },
    { key: 'balance', label: 'Amount Due', width: 115, type: 'money', align: 'right', get: b => (b.status === 'void' ? 0 : b.is_credit ? -r2(b.balance) : b.balance) },
    { key: 'status', label: 'Status', width: 100, get: statusOf, render: b => <span style={{ color: STATUS_COLOR[statusOf(b)], fontWeight: 600 }}>{statusOf(b)}</span> },
    { key: 'has_attachment', label: '📎', width: 44, type: 'bool', text: b => (b.has_attachment ? 'Yes' : ''), render: b => (b.has_attachment ? '📎' : '') },
  ];
  const payCols = [
    { key: 'payment_date', label: 'Date', width: 105, type: 'date', get: p => p.payment_date + 'T00:00:00' },
    { key: 'vendor_name', label: 'Vendor', width: 200, render: p => <strong>{p.vendor_name}</strong> },
    { key: 'method', label: 'Paid By', width: 150, get: p => METHOD[p.method] || p.method },
    { key: 'check_number', label: 'Check #', width: 95, get: p => (p.to_print ? 'To print' : p.check_number || '') },
    { key: 'voided_at', label: 'Status', width: 80, get: p => (p.voided_at ? 'Void' : ''), render: p => (p.voided_at ? <span style={{ color: 'var(--gray-500)', fontWeight: 600 }}>VOID</span> : '') },
    { key: 'account_name', label: 'From Account', width: 180 },
    { key: 'bills', label: 'Bills Paid', width: 200 },
    { key: 'memo', label: 'Memo', width: 160, defaultHidden: true },
    { key: 'amount', label: 'Amount', width: 110, type: 'money', align: 'right' },
  ];

  const Stat = ({ label, value, sub, color, onClick }) => (
    <div className="stat-card" style={{ cursor: onClick ? 'pointer' : undefined }} onClick={onClick}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      {sub ? <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{sub}</div> : null}
    </div>
  );

  return (
    <div>
      {sum && (
        <div className="stat-grid" style={{ marginBottom: 14 }}>
          <Stat label="Unpaid Bills" value={money(sum.open_total)} sub={`${sum.open_count} bill${sum.open_count === 1 ? '' : 's'}${parseFloat(sum.credits_total) > 0 ? ` · ${money(sum.credits_total)} vendor credits` : ''}`} />
          <Stat label="Overdue" value={money(sum.overdue_total)} sub={`${sum.overdue_count} bill${sum.overdue_count === 1 ? '' : 's'}`} color={sum.overdue_count ? 'var(--danger)' : undefined} />
          <Stat label="Due in the Next 7 Days" value={money(sum.due_soon_total)} sub={`${sum.due_soon_count} bill${sum.due_soon_count === 1 ? '' : 's'}`} />
          <Stat label="Checks to Print" value={sum.to_print} sub={sum.to_print ? 'Click to print them' : 'None waiting'} onClick={sum.to_print ? () => setPrinting({}) : undefined} />
        </div>
      )}
      {view === 'bills' ? (
        <DataGrid
          gridKey="ap-bills"
          rows={rows}
          loading={loading}
          columns={billCols}
          onRowClick={b => setOpenBill(b.id)}
          totals={['total_amount', 'amount_paid', 'balance']}
          defaultSort={{ key: 'due_date', dir: 'asc' }}
          emptyText={status === 'open' ? 'No unpaid bills. 🎉' : 'No bills yet — press “+ Enter Bill” when a vendor invoice comes in.'}
          toolbarLeft={(
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <select className="form-select" style={{ width: 150 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="open">Unpaid bills</option>
                <option value="paid">Paid bills</option>
                <option value="credits">Vendor credits</option>
                <option value="void">Voided</option>
                <option value="all">All bills</option>
              </select>
              <button className="btn btn-ghost btn-sm" onClick={() => setView('payments')}>Bill payments →</button>
            </span>
          )}
          toolbarRight={(
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {canEdit && <button className="btn btn-primary btn-sm" onClick={() => setOpenBill('new')}>+ Enter Bill</button>}
              {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => setPaying({})}>💵 Pay Bills</button>}
              <button className="btn btn-secondary btn-sm" onClick={() => setPrinting({})}>🖨 Print Checks{sum?.to_print ? ` (${sum.to_print})` : ''}</button>
              <button className="btn btn-ghost btn-sm" title="Check paper & alignment" onClick={() => setSetup(true)}>⚙</button>
            </span>
          )}
        />
      ) : (
        <DataGrid
          gridKey="ap-payments"
          rows={payments}
          loading={loading}
          columns={payCols}
          onRowClick={p => setOpenPayment(p.id)}
          totals={['amount']}
          defaultSort={{ key: 'payment_date', dir: 'desc' }}
          emptyText="No bill payments yet."
          toolbarLeft={<button className="btn btn-ghost btn-sm" onClick={() => setView('bills')}>← Bills</button>}
          toolbarRight={canEdit && <button className="btn btn-secondary btn-sm" onClick={() => setPaying({})}>💵 Pay Bills</button>}
        />
      )}
      <p className="form-hint" style={{ marginTop: 8 }}>
        Enter a bill when a vendor’s invoice arrives; it shows as money owed (Accounts Payable). Pay it later with a check, an online transfer or a credit card —
        the payment lands in that bank or card register. Checks marked “To be printed” wait under Print Checks.
      </p>

      {openBill && <BillModal billId={openBill === 'new' ? null : openBill} canEdit={canEdit} onClose={() => setOpenBill(null)}
        onSaved={() => { setOpenBill(null); load(); }}
        onPay={(b) => { setOpenBill(null); setPaying({ preselect: [b.id] }); }}
        onOpenPayment={(id) => { setOpenBill(null); setOpenPayment(id); }} />}
      {openPayment && <BillPaymentModal paymentId={openPayment} canEdit={canEdit} onClose={() => setOpenPayment(null)} onSaved={() => { setOpenPayment(null); load(); }} />}
      {paying && <PayBillsModal canEdit={canEdit} preselect={paying.preselect} onClose={() => setPaying(null)}
        onPaid={(created, toPrint) => { setPaying(null); load(); if (toPrint && created.length) setPrinting({ preselect: created.map(c => ({ source: 'bill_payment', id: c.id })) }); }} />}
      {printing && <PrintChecksModal canEdit={canEdit} preselect={printing.preselect} onClose={() => setPrinting(null)} onDone={load} />}
      {setup && <CheckSettingsModal canEdit={canEdit} onClose={() => setSetup(false)} />}
    </div>
  );
}

// ─── Enter / edit a bill ──────────────────────────────────────────────────────
export function BillModal({ billId, vendorId: vendor0, canEdit, onClose, onSaved, onPay, onOpenPayment }) {
  const isEdit = !!billId;
  const [vendors, setVendors] = useVendors();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [bill, setBill] = useState(null);
  const [newVendor, setNewVendor] = useState(false);
  const [booksStart, setBooksStart] = useState(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ vendorId: vendor0 || null, billDate: today(), terms: '', dueDate: '', billNumber: '', notes: '' });
  const [lines, setLines] = useState([{ accountId: null, description: '', amount: '' }]);
  const [dueTouched, setDueTouched] = useState(false);
  const [isCredit, setIsCredit] = useState(false); // [marker: books-pro v1] vendor credit instead of a bill
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const voided = bill?.status === 'void';
  const ro = !canEdit || voided;

  const loadBill = useCallback(() => api.get(`/ap/bills/${billId}`).then(r => {
    const b = r.data; setBill(b); setIsCredit(!!b.is_credit);
    setForm({ vendorId: b.vendor_id, billDate: b.bill_date, terms: b.terms || '', dueDate: b.due_date || '', billNumber: b.bill_number || '', notes: b.notes || '' });
    setLines(b.lines.length ? b.lines.map(l => ({ accountId: l.account_id, classId: l.class_id || null, description: l.description || '', amount: String(r2(l.amount)) })) : [{ accountId: null, classId: null, description: '', amount: '' }]);
    setDueTouched(true);
  }).catch(() => toast.error('Could not load the bill.')).finally(() => setLoading(false)), [billId]);
  useEffect(() => {
    if (isEdit) loadBill();
    api.get('/ap/summary').then(r => setBooksStart(r.data.books_start)).catch(() => {});
  }, [isEdit, loadBill]);

  // picking a vendor fills its terms and usual account
  const pickVendor = (id) => {
    const v = vendors.find(x => x.id === id);
    setForm(f => ({ ...f, vendorId: id, terms: (!isEdit && v?.payment_terms) ? (TERMS.find(t => t.toLowerCase() === v.payment_terms.toLowerCase()) || v.payment_terms) : f.terms }));
    if (v?.default_account_id) setLines(ls => (ls.length === 1 && !ls[0].accountId ? [{ ...ls[0], accountId: v.default_account_id }] : ls));
  };
  // due date follows the bill date + terms until it's typed over
  useEffect(() => {
    if (dueTouched) return;
    const d = termDays(form.terms);
    if (d !== null && form.billDate) setForm(f => ({ ...f, dueDate: addDays(f.billDate, d) }));
  }, [form.terms, form.billDate, dueTouched]);

  const total = r2(lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const paid = r2(bill?.amount_paid);
  const locked = paid > 0; // vendor can't change once paid

  const uploadFile = async (id, f) => {
    const fd = new FormData(); fd.append('file', f);
    await api.post(`/ap/bills/${id}/attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };
  const save = async (andNew, allowDuplicate) => {
    if (!form.vendorId) return toast.error('Choose the vendor.');
    const body = { ...form, isCredit, dueDate: isCredit ? null : (form.dueDate || null), allowDuplicate: !!allowDuplicate,
                   lines: lines.map(l => ({ accountId: l.accountId, classId: l.classId || null, description: l.description, amount: r2(l.amount) })) };
    setSaving(true);
    try {
      const r = isEdit ? await api.patch(`/ap/bills/${billId}`, body) : await api.post('/ap/bills', body);
      if (file) { try { await uploadFile(r.data.id, file); } catch (e) { toast.error('The bill was saved, but the file didn’t upload: ' + (e.response?.data?.error || 'try again')); } }
      toast.success(isEdit ? 'Saved.' : `${isCredit ? 'Credit' : 'Bill'} from ${vendors.find(v => v.id === form.vendorId)?.name || 'vendor'} entered — ${money(total)}.`);
      if (andNew) {
        setForm({ vendorId: null, billDate: form.billDate, terms: '', dueDate: '', billNumber: '', notes: '' });
        setLines([{ accountId: null, description: '', amount: '' }]); setFile(null); setDueTouched(false);
        onSaved && onSaved(r.data, true);
      } else onSaved && onSaved(r.data);
    } catch (err) {
      if (err.response?.data?.code === 'DUPLICATE_BILL') { if (window.confirm(err.response.data.error)) return save(andNew, true); }
      else toast.error(err.response?.data?.error || 'Could not save the bill.');
    } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this bill? This cannot be undone.')) return;
    try { await api.delete(`/ap/bills/${billId}`); toast.success('Bill deleted.'); onSaved && onSaved(null); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
  };
  const voidBill = async () => {
    if (!window.confirm(`Void this ${isCredit ? 'credit' : 'bill'}? It stays on file marked VOID, but no longer counts.`)) return;
    try { await api.post(`/ap/bills/${billId}/void`); toast.success('Voided.'); onSaved && onSaved(null); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not void.'); }
  };
  const unapply = async (a) => {
    if (!window.confirm('Remove this credit from the bill? The bill becomes unpaid by that amount again.')) return;
    try { await api.delete(`/ap/credit-applications/${a.id}`); toast.success('Credit removed.'); loadBill(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not remove it.'); }
  };
  const viewFile = async () => {
    try {
      const r = await api.get(`/ap/bills/${billId}/attachment`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open the file.'); }
  };
  const attachNow = async (f) => {
    if (!f) return;
    try { await uploadFile(billId, f); toast.success('File attached.'); loadBill(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not attach the file.'); }
  };
  const removeFile = async () => {
    if (!window.confirm('Remove the attached file?')) return;
    try { await api.delete(`/ap/bills/${billId}/attachment`); loadBill(); } catch { toast.error('Could not remove it.'); }
  };

  const vendorList = vendors.filter(v => v.is_active || v.id === form.vendorId).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <>
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1550 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? `${isCredit ? 'Credit' : 'Bill'}${bill?.bill_number ? ' ' + bill.bill_number : ''} — ${bill?.vendor_name || ''}${voided ? ' (VOID)' : ''}` : isCredit ? 'Enter Vendor Credit' : 'Enter Bill'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          {loading ? <div className="spinner" /> : (
            <>
              {!isEdit && canEdit && (
                <div style={{ display: 'flex', gap: 18, fontSize: '0.875rem', marginBottom: 10 }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={!isCredit} onChange={() => setIsCredit(false)} /> Bill</label>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={isCredit} onChange={() => setIsCredit(true)} /> Credit
                    <span className="form-hint" style={{ margin: 0 }}>(the vendor owes us — e.g. a refund or a returned item; used up in Pay Bills)</span></label>
                </div>
              )}
              {voided && <div style={{ fontSize: '0.85rem', background: 'var(--gray-100)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                <strong>VOID</strong> — kept for the record; it no longer counts anywhere.</div>}
              {isCredit && bill && (bill.credits || []).length > 0 && (
                <div style={{ fontSize: '0.85rem', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                  {r2(bill.balance) <= 0 ? <strong>Fully used. </strong> : <><strong>{money(bill.balance)}</strong> still available. </>}
                  {bill.credits.map(a => (
                    <span key={a.id} style={{ marginRight: 10 }}>Used {money(a.amount)} on bill {a.to_bill_number || fmtDay(a.to_bill_date)}
                      {canEdit && !a.bill_payment_id && <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', color: 'var(--danger)' }} onClick={() => unapply(a)}>remove</button>}
                    </span>
                  ))}
                </div>
              )}
              {!isCredit && bill && (bill.credits || []).length > 0 && (
                <div style={{ fontSize: '0.85rem', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                  Vendor credits used: {bill.credits.map(a => (
                    <span key={a.id} style={{ marginRight: 10 }}>{a.credit_number || 'credit'} {money(a.amount)}
                      {canEdit && !a.bill_payment_id && <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', color: 'var(--danger)' }} onClick={() => unapply(a)}>remove</button>}
                    </span>
                  ))}
                </div>
              )}
              {bill && !isCredit && paid > 0 && (
                <div style={{ fontSize: '0.85rem', background: r2(bill.balance) <= 0 ? '#f0fdf4' : '#fffbeb', border: `1px solid ${r2(bill.balance) <= 0 ? '#bbf7d0' : '#fde68a'}`, borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                  {r2(bill.balance) <= 0 ? <>✓ <strong>Paid in full.</strong></> : <>Partly paid — <strong>{money(bill.balance)}</strong> still owed.</>}
                  {' '}{bill.payments.map(p => (
                    <button key={p.id} className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => onOpenPayment && onOpenPayment(p.id)}>
                      {fmtDay(p.payment_date)} · {METHOD[p.method]}{p.check_number ? ` #${p.check_number}` : p.to_print ? ' (to print)' : ''} · {money(p.amount)}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Vendor *</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select className="form-select" value={form.vendorId || ''} disabled={ro || locked} onChange={e => pickVendor(e.target.value ? parseInt(e.target.value, 10) : null)}>
                      <option value="">— Choose a vendor —</option>
                      {vendorList.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                    {!ro && !locked && <button type="button" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => setNewVendor(true)}>+ New</button>}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{isCredit ? 'Credit Date *' : 'Bill Date *'}</label>
                  <input className="form-input" type="date" value={form.billDate} disabled={ro} onChange={e => set('billDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ref. No. (their invoice #)</label>
                  <input className="form-input" value={form.billNumber} disabled={ro} onChange={e => set('billNumber', e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isCredit ? '1fr' : '1fr 1fr 2fr', gap: 12 }}>
                {!isCredit && <div className="form-group">
                  <label className="form-label">Terms</label>
                  <select className="form-select" value={form.terms} disabled={ro} onChange={e => { setDueTouched(false); set('terms', e.target.value); }}>
                    <option value="">—</option>
                    {[...TERMS, ...(form.terms && !TERMS.includes(form.terms) ? [form.terms] : [])].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>}
                {!isCredit && <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input className="form-input" type="date" value={form.dueDate} disabled={ro} onChange={e => { setDueTouched(true); set('dueDate', e.target.value); }} />
                </div>}
                <div className="form-group">
                  <label className="form-label">Memo</label>
                  <input className="form-input" value={form.notes} disabled={ro} onChange={e => set('notes', e.target.value)} placeholder="e.g. Photography — Legislative Luncheon" />
                </div>
              </div>
              {booksStart && form.billDate && form.billDate < booksStart && (
                <p className="form-hint" style={{ color: '#b45309', marginTop: -6 }}>
                  This bill is dated before {fmtDay(booksStart)}, when the books here begin. It’s kept as an unpaid bill so you can pay it, but the
                  expense itself stays in the QuickBooks history (it isn’t counted twice). The payment is recorded normally.
                </p>
              )}

              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', margin: '4px 0' }}>{isCredit ? 'What the credit is for' : 'What the bill is for'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.5fr 1.7fr 1fr auto', gap: 6, fontSize: '0.72rem', color: 'var(--gray-500)', marginBottom: 2 }}>
                <span>Account</span><span>Class (event / program)</span><span>Description</span><span style={{ textAlign: 'right' }}>Amount</span><span />
              </div>
              {lines.map((l, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.5fr 1.7fr 1fr auto', gap: 6, marginBottom: 6 }}>
                  <AccountSelect value={l.accountId} disabled={ro} onChange={v => setLine(i, 'accountId', v)}
                                 types={['expense', 'other_expense', 'cost_of_goods_sold', 'fixed_asset', 'asset', 'other_asset', 'other_liability', 'loan', 'credit_card', 'equity', 'income', 'other_income']}
                                 placeholder="— Expense account —" />
                  <ClassSelect value={l.classId} disabled={ro} onChange={v => setLine(i, 'classId', v)} />
                  <input className="form-input" value={l.description} disabled={ro} onChange={e => setLine(i, 'description', e.target.value)} />
                  <input className="form-input" type="number" step="0.01" style={{ textAlign: 'right' }} value={l.amount} disabled={ro} onChange={e => setLine(i, 'amount', e.target.value)} />
                  <button className="btn btn-ghost btn-sm" disabled={ro || lines.length === 1} onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {!ro ? <button className="btn btn-secondary btn-sm" onClick={() => setLines(ls => [...ls, { accountId: null, classId: ls[ls.length - 1]?.classId || null, description: '', amount: '' }])}>+ Add line</button> : <span />}
                <span style={{ fontSize: '1rem' }}>{isCredit ? 'Credit amount' : 'Amount due'}: <strong>{money(total)}</strong></span>
              </div>

              <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 6, fontSize: '0.85rem', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>📎 Vendor’s invoice:</span>
                {bill?.has_attachment ? (
                  <>
                    <button className="btn btn-ghost btn-sm" onClick={viewFile}>Open {bill.attachment_name || 'file'}</button>
                    {!ro && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={removeFile}>Remove</button>}
                  </>
                ) : !ro ? (
                  <input type="file" accept="application/pdf,image/*" onChange={e => (isEdit ? attachNow(e.target.files[0]) : setFile(e.target.files[0] || null))} />
                ) : <span style={{ color: 'var(--gray-500)' }}>none</span>}
                {!isEdit && file && <span style={{ color: 'var(--gray-500)' }}>(attached when you save)</span>}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', gap: 4 }}>
            {isEdit && canEdit && !voided && <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={remove}>Delete</button>}
            {isEdit && canEdit && !voided && <button className="btn btn-ghost" onClick={voidBill}>Void</button>}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
            {isEdit && canEdit && bill && !isCredit && !voided && r2(bill.balance) > 0 && onPay && <button className="btn btn-secondary" onClick={() => onPay(bill)}>💵 Pay This Bill</button>}
            {!isEdit && canEdit && <button className="btn btn-secondary" onClick={() => save(true)} disabled={saving}>Save &amp; New</button>}
            {canEdit && !voided && <button className="btn btn-primary" onClick={() => save(false)} disabled={saving || loading}>{saving ? 'Saving…' : 'Save'}</button>}
          </span>
        </div>
      </div>
    </div>
    {newVendor && (
      <VendorModal zIndex={1650} onClose={() => setNewVendor(false)}
        onSaved={(v) => { setNewVendor(false); if (v) { setVendors(vs => [...vs, v]); pickVendor(v.id); setForm(f => ({ ...f, vendorId: v.id })); } }} />
    )}
    </>
  );
}

// ─── Pay Bills ────────────────────────────────────────────────────────────────
export function PayBillsModal({ canEdit, preselect, onClose, onPaid }) {
  const [bills, setBills] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showAll, setShowAll] = useState(true);
  const [dueBy, setDueBy] = useState(addDays(today(), 7));
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('due');
  const [picked, setPicked] = useState(() => new Set(preselect || []));
  const [amounts, setAmounts] = useState({});
  const [credits, setCredits] = useState({});          // [marker: books-pro v1] billId → vendor credit used
  const [available, setAvailable] = useState({});      // vendorId → vendor credits available
  const [pay, setPay] = useState({ date: today(), method: 'check', accountId: null, toPrint: true, firstNumber: '', memo: '' });
  const [booksStart, setBooksStart] = useState(null);
  const [busy, setBusy] = useState(false);
  const setP = (k, v) => setPay(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/ap/bills?status=open').then(r => setBills((r.data || []).filter(b => !b.is_credit))).catch(() => setBills([]));
    api.get('/ap/bills?status=credits').then(r => {
      const m = {}; (r.data || []).forEach(c => { m[c.vendor_id] = r2((m[c.vendor_id] || 0) + r2(c.balance)); }); setAvailable(m);
    }).catch(() => {});
    api.get('/bookkeeping/register-accounts').then(r => {
      const list = (r.data || []).filter(a => ['bank', 'credit_card'].includes(a.account_type));
      setAccounts(list);
      setPay(p => ({ ...p, accountId: p.accountId || list.find(a => a.account_type === 'bank')?.id || null }));
    }).catch(() => {});
    api.get('/ap/summary').then(r => setBooksStart(r.data.books_start)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!pay.accountId || pay.method === 'credit_card') return;
    api.get(`/ap/checks/next-number?accountId=${pay.accountId}`).then(r => setP('firstNumber', r.data.next || '')).catch(() => {});
  }, [pay.accountId, pay.method]);

  const cr = (b) => r2(credits[b.id]);
  const amt = (b) => (amounts[b.id] !== undefined ? amounts[b.id] : String(r2(r2(b.balance) - cr(b))));
  const usedBy = (vendorId, exceptId) => r2((bills || []).filter(b => b.vendor_id === vendorId && picked.has(b.id) && b.id !== exceptId).reduce((s, b) => s + cr(b), 0));
  // use each vendor's credits on the ticked bills, oldest due first
  const useCredits = () => {
    const next = { ...credits }; const left = { ...available };
    (bills || []).filter(b => picked.has(b.id)).sort((a, b) => String(a.due_date || a.bill_date).localeCompare(String(b.due_date || b.bill_date))).forEach(b => {
      const room = r2(r2(b.balance) - r2(next[b.id]));
      const use = r2(Math.min(room, r2(left[b.vendor_id]) - 0));
      if (use > 0) { next[b.id] = r2(r2(next[b.id]) + use); left[b.vendor_id] = r2(left[b.vendor_id] - use); }
    });
    setCredits(next); setAmounts({});
  };
  const shown = useMemo(() => {
    let list = (bills || []).filter(b => showAll || picked.has(b.id) || !b.due_date || b.due_date <= dueBy);
    if (q.trim()) { const t = q.trim().toLowerCase(); list = list.filter(b => picked.has(b.id) || `${b.vendor_name} ${b.bill_number || ''} ${b.notes || ''}`.toLowerCase().includes(t)); }
    return list.slice().sort((a, b) => (sort === 'vendor' ? a.vendor_name.localeCompare(b.vendor_name) || String(a.due_date).localeCompare(String(b.due_date))
      : String(a.due_date || a.bill_date).localeCompare(String(b.due_date || b.bill_date))));
  }, [bills, showAll, dueBy, q, sort, picked]);
  const chosen = (bills || []).filter(b => picked.has(b.id));
  const total = r2(chosen.reduce((s, b) => s + (parseFloat(amt(b)) || 0), 0));
  const creditTotal = r2(chosen.reduce((s, b) => s + cr(b), 0));
  const vendorsCount = new Set(chosen.filter(b => (parseFloat(amt(b)) || 0) > 0).map(b => b.vendor_id)).size;
  const creditsToUse = chosen.some(b => r2(available[b.vendor_id]) - usedBy(b.vendor_id, null) > 0.004 && r2(b.balance) - cr(b) > 0.004);
  const acctList = accounts.filter(a => (pay.method === 'credit_card' ? a.account_type === 'credit_card' : a.account_type === 'bank'));
  const acct = accounts.find(a => a.id === pay.accountId);
  const toggle = (b) => setPicked(p => { const n = new Set(p); n.has(b.id) ? n.delete(b.id) : n.add(b.id); return n; });
  const setMethod = (m) => setPay(p => {
    const list = accounts.filter(a => (m === 'credit_card' ? a.account_type === 'credit_card' : a.account_type === 'bank'));
    return { ...p, method: m, accountId: list.some(a => a.id === p.accountId) ? p.accountId : (list[0]?.id || null), toPrint: m === 'check' ? p.toPrint : false };
  });

  const submit = async () => {
    if (!chosen.length) return toast.error('Tick the bills to pay.');
    if (!pay.accountId) return toast.error(pay.method === 'credit_card' ? 'Choose the credit card.' : 'Choose the bank account.');
    for (const b of chosen) {
      const a = r2(amt(b));
      if (a < 0 || (a === 0 && cr(b) <= 0)) return toast.error(`Enter the amount to pay for ${b.vendor_name}.`);
      if (a + cr(b) > r2(b.balance) + 0.005) return toast.error(`${b.vendor_name}: that’s more than the ${money(b.balance)} still owed.`);
      if (usedBy(b.vendor_id, null) > r2(available[b.vendor_id]) + 0.005) return toast.error(`${b.vendor_name}: only ${money(available[b.vendor_id])} of credits are available.`);
    }
    const how = pay.method === 'check' ? (pay.toPrint ? 'check(s) to print' : `check(s) starting #${pay.firstNumber || '—'}`) : pay.method === 'credit_card' ? `${acct?.name}` : `transfer from ${acct?.name}`;
    if (!window.confirm(`Pay ${chosen.length} bill${chosen.length === 1 ? '' : 's'}: ${money(total)} to ${vendorsCount} vendor${vendorsCount === 1 ? '' : 's'} by ${how}${creditTotal ? `, plus ${money(creditTotal)} of vendor credits` : ''}?`)) return;
    setBusy(true);
    try {
      const r = await api.post('/ap/pay', { ...pay, bills: chosen.map(b => ({ billId: b.id, amount: r2(amt(b)), credit: cr(b) })) });
      toast.success(r.data.payments.length ? `Paid — ${r.data.payments.length} payment${r.data.payments.length === 1 ? '' : 's'} recorded in ${acct?.name || 'the register'}.` : 'Credits applied.');
      onPaid && onPaid(r.data.payments, pay.method === 'check' && pay.toPrint);
    } catch (err) { toast.error(err.response?.data?.error || 'Could not record the payment.'); }
    finally { setBusy(false); }
  };

  const th = { textAlign: 'left', padding: '6px 8px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', borderBottom: '1px solid var(--gray-200)', position: 'sticky', top: 0, background: 'white' };
  const td = { padding: '5px 8px', borderTop: '1px solid var(--gray-100)' };
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1400 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 1040 }}>
        <div className="modal-header"><span className="modal-title">Pay Bills</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontSize: '0.875rem' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={!showAll} onChange={() => setShowAll(false)} /> Due on or before
              <input type="date" className="form-input" style={{ height: 30, width: 150 }} value={dueBy} onChange={e => { setDueBy(e.target.value); setShowAll(false); }} /></label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={showAll} onChange={() => setShowAll(true)} /> Show all bills</label>
            <input className="form-input" style={{ height: 30, width: 220 }} placeholder="🔍 Search vendor, ref. no., memo" value={q} onChange={e => setQ(e.target.value)} />
            <select className="form-select" style={{ height: 30, width: 'auto' }} value={sort} onChange={e => setSort(e.target.value)}>
              <option value="due">Sort by due date</option><option value="vendor">Sort by vendor</option>
            </select>
          </div>
          <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 300, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead><tr>
                <th style={{ ...th, width: 30 }}><input type="checkbox" checked={shown.length > 0 && shown.every(b => picked.has(b.id))}
                  onChange={e => setPicked(p => { const n = new Set(p); shown.forEach(b => (e.target.checked ? n.add(b.id) : n.delete(b.id))); return n; })} /></th>
                <th style={th}>Date Due</th><th style={th}>Vendor</th><th style={th}>Ref. No.</th><th style={th}>Memo</th>
                <th style={{ ...th, textAlign: 'right' }}>Amt. Due</th><th style={{ ...th, textAlign: 'right', width: 120 }}>Credits Used</th><th style={{ ...th, textAlign: 'right', width: 130 }}>Amt. To Pay</th>
              </tr></thead>
              <tbody>
                {bills === null && <tr><td colSpan={8} style={td}><div className="spinner" /></td></tr>}
                {bills && shown.length === 0 && <tr><td colSpan={8} style={{ ...td, color: 'var(--gray-500)', padding: 16 }}>{bills.length ? 'No bills match.' : 'No unpaid bills.'}</td></tr>}
                {shown.map(b => (
                  <tr key={b.id} style={{ background: picked.has(b.id) ? '#eff6ff' : undefined }}>
                    <td style={td}><input type="checkbox" checked={picked.has(b.id)} onChange={() => toggle(b)} /></td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: b.overdue ? 'var(--danger)' : undefined }}>{b.due_date ? fmtDay(b.due_date) : '—'}</td>
                    <td style={td} onClick={() => toggle(b)}><strong>{b.vendor_name}</strong></td>
                    <td style={td}>{b.bill_number}</td>
                    <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.notes || ''}>{b.notes}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(b.balance)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {picked.has(b.id) && r2(available[b.vendor_id]) > 0
                        ? <input className="form-input" type="number" step="0.01" min="0" style={{ height: 28, textAlign: 'right' }} value={credits[b.id] || ''} placeholder="0.00"
                                 title={`${money(available[b.vendor_id])} of credits from this vendor`}
                                 onChange={e => { setCredits(c => ({ ...c, [b.id]: e.target.value })); setAmounts(a => { const n = { ...a }; delete n[b.id]; return n; }); }} />
                        : <span style={{ color: 'var(--gray-400)' }}>{r2(available[b.vendor_id]) > 0 ? money(available[b.vendor_id]) + ' avail.' : '—'}</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {picked.has(b.id)
                        ? <input className="form-input" type="number" step="0.01" style={{ height: 28, textAlign: 'right' }} value={amt(b)} onChange={e => setAmounts(a => ({ ...a, [b.id]: e.target.value }))} />
                        : <span style={{ color: 'var(--gray-400)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0 14px', fontSize: '0.875rem' }}>
            <span style={{ color: 'var(--gray-600)', display: 'flex', gap: 10, alignItems: 'center' }}>
              {chosen.length} bill{chosen.length === 1 ? '' : 's'} picked{vendorsCount > 1 ? ` · ${vendorsCount} vendors — one payment each` : ''}
              {creditsToUse && <button className="btn btn-secondary btn-sm" onClick={useCredits}>Use available vendor credits</button>}
            </span>
            <span>{creditTotal ? <>Credits {money(creditTotal)} · </> : null}Total to pay: <strong>{money(total)}</strong></span>
          </div>

          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: 6 }}>Payment</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.6fr', gap: 12 }}>
            <div className="form-group"><label className="form-label">Date</label>
              <input className="form-input" type="date" value={pay.date} onChange={e => setP('date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Method</label>
              <select className="form-select" value={pay.method} onChange={e => setMethod(e.target.value)}>
                {Object.entries(METHOD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">{pay.method === 'credit_card' ? 'Credit card' : 'Pay from (bank account)'}</label>
              <select className="form-select" value={pay.accountId || ''} onChange={e => setP('accountId', e.target.value ? parseInt(e.target.value, 10) : null)}>
                <option value="">—</option>
                {acctList.map(a => <option key={a.id} value={a.id}>{a.name}{a.account_last_four ? ` ••${a.account_last_four}` : ''}</option>)}
              </select>
              {acct && <p className="form-hint">{pay.method === 'credit_card' ? 'Balance owed' : 'Balance'} {money(acct.display_balance)} → after: <strong>{money(pay.method === 'credit_card' ? r2(acct.display_balance) + total : r2(acct.display_balance) - total)}</strong></p>}
            </div>
          </div>
          {pay.method === 'check' && (
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', fontSize: '0.875rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={pay.toPrint} onChange={() => setP('toPrint', true)} /> To be printed</label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={!pay.toPrint} onChange={() => setP('toPrint', false)} /> Assign check number (hand-written)</label>
              {!pay.toPrint && <input className="form-input" style={{ width: 120, height: 30 }} value={pay.firstNumber} onChange={e => setP('firstNumber', e.target.value)} placeholder="Check #" />}
              {!pay.toPrint && vendorsCount > 1 && <span className="form-hint" style={{ margin: 0 }}>Next vendors get the following numbers.</span>}
            </div>
          )}
          <div className="form-group" style={{ marginTop: 10 }}><label className="form-label">Memo (optional — otherwise our account # with the vendor)</label>
            <input className="form-input" value={pay.memo} onChange={e => setP('memo', e.target.value)} /></div>
          {booksStart && pay.date < booksStart && <p className="form-hint" style={{ color: '#b45309' }}>This date is before {fmtDay(booksStart)} — the payment won’t be added to the books (QuickBooks history covers it).</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !canEdit || !chosen.length}>{busy ? 'Paying…' : `Pay Selected Bills${chosen.length ? ` (${money(total)})` : ''}`}</button>
        </div>
      </div>
    </div>
  );
}

// ─── One bill payment ─────────────────────────────────────────────────────────
export function BillPaymentModal({ paymentId, canEdit, onClose, onSaved }) {
  const [p, setPmt] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const load = useCallback(() => api.get(`/ap/payments/${paymentId}`).then(r => {
    setPmt(r.data);
    setForm({ date: r.data.payment_date, method: r.data.method, accountId: r.data.account_id, checkNumber: r.data.check_number || '', toPrint: !!r.data.to_print, memo: r.data.memo || '' });
  }).catch(() => toast.error('Could not load the payment.')), [paymentId]);
  useEffect(() => {
    load();
    api.get('/bookkeeping/register-accounts').then(r => setAccounts((r.data || []).filter(a => ['bank', 'credit_card'].includes(a.account_type)))).catch(() => {});
  }, [load]);

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/ap/payments/${paymentId}`, form); toast.success('Payment saved.'); onSaved && onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this payment? The bill(s) it paid become unpaid again. If a check was already printed, void it by hand.')) return;
    setBusy(true);
    try { await api.delete(`/ap/payments/${paymentId}`); toast.success('Payment deleted — the bill is open again.'); onSaved && onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
    finally { setBusy(false); }
  };
  const reprint = async () => {
    try { await printOneCheck('bill_payment', paymentId); }
    catch (e) { toast.error(e.message || 'Could not print.'); }
  };
  // [marker: books-pro v1] void: keeps the check (and its number) in the register at $0, bills become unpaid again
  const voidIt = async () => {
    const reason = window.prompt('Void this payment? The bill(s) become unpaid again and the check stays in the register at $0 marked VOID.\n\nReason (optional):', '');
    if (reason === null) return;
    setBusy(true);
    try { const r = await api.post(`/ap/payments/${paymentId}/void`, { reason }); toast.success(r.data.message || 'Voided.'); onSaved && onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not void.'); }
    finally { setBusy(false); }
  };

  const voided = !!p?.voided_at;
  const locked = !!p?.reconciled || voided;
  const acctList = accounts.filter(a => (form?.method === 'credit_card' ? a.account_type === 'credit_card' : a.account_type === 'bank'));
  return (
    <>
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1560 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <div className="modal-header"><span className="modal-title">Bill Payment{p ? ` — ${p.vendor_name}` : ''}</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {!p || !form ? <div className="spinner" /> : (
            <>
              {voided ? <div style={{ fontSize: '0.85rem', background: 'var(--gray-100)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                <strong>VOID</strong> — was {money(p.original_amount)}{p.void_reason ? ` (${p.void_reason})` : ''}. It stays in the register at $0.</div>
              : locked && <div style={{ fontSize: '0.82rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
                This payment is reconciled. You can fix the check number and memo; the rest is locked.</div>}
              {(p.credits || []).length > 0 && <p className="form-hint" style={{ marginTop: 0 }}>Vendor credits used with this payment: {p.credits.map(c => `${c.credit_number || 'credit'} ${money(c.amount)}`).join(', ')}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.6fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Date</label>
                  <input className="form-input" type="date" value={form.date} disabled={locked || !canEdit} onChange={e => set('date', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Method</label>
                  <select className="form-select" value={form.method} disabled={locked || !canEdit} onChange={e => { const m = e.target.value; const list = accounts.filter(a => (m === 'credit_card' ? a.account_type === 'credit_card' : a.account_type === 'bank')); setForm(f => ({ ...f, method: m, toPrint: m === 'check' ? f.toPrint : false, accountId: list.some(a => a.id === f.accountId) ? f.accountId : list[0]?.id })); }}>
                    {Object.entries(METHOD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select></div>
                <div className="form-group"><label className="form-label">Account</label>
                  <select className="form-select" value={form.accountId || ''} disabled={locked || !canEdit} onChange={e => set('accountId', parseInt(e.target.value, 10))}>
                    {acctList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select></div>
              </div>
              {form.method === 'check' && (
                <div style={{ display: 'flex', gap: 18, alignItems: 'center', fontSize: '0.875rem', marginBottom: 10, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={form.toPrint} disabled={locked || !canEdit} onChange={() => set('toPrint', true)} /> To be printed</label>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={!form.toPrint} disabled={!canEdit} onChange={() => set('toPrint', false)} /> Check #</label>
                  {!form.toPrint && <input className="form-input" style={{ width: 120, height: 30 }} value={form.checkNumber} disabled={!canEdit} onChange={e => set('checkNumber', e.target.value)} />}
                  {p.printed_at && <span className="form-hint" style={{ margin: 0 }}>Printed {fmtDay(p.printed_at)}</span>}
                </div>
              )}
              <div className="form-group"><label className="form-label">Memo</label>
                <input className="form-input" value={form.memo} disabled={!canEdit} onChange={e => set('memo', e.target.value)} /></div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Bill Date</th><th>Ref. No.</th><th>Due</th><th style={{ textAlign: 'right' }}>Bill Amount</th><th style={{ textAlign: 'right' }}>Paid Here</th></tr></thead>
                  <tbody>
                    {p.lines.map(l => (
                      <tr key={l.id}><td>{fmtDay(l.bill_date)}</td><td>{l.bill_number}</td><td>{l.due_date ? fmtDay(l.due_date) : '—'}</td>
                        <td style={{ textAlign: 'right' }}>{money(l.total_amount)}</td><td style={{ textAlign: 'right' }}>{money(l.amount)}</td></tr>
                    ))}
                    <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{money(p.amount)}</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="form-hint">To change the amounts, delete this payment and pay the bills again.</p>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', gap: 4 }}>
            {canEdit && p && !locked && <button className="btn btn-ghost" onClick={voidIt} disabled={busy}>Void</button>}
            {canEdit && p && !locked && <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={remove} disabled={busy}>Delete</button>}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            {p && !voided && p.method === 'check' && (p.to_print
              ? <button className="btn btn-secondary" onClick={() => setPrinting(true)}>🖨 Print Check</button>
              : <button className="btn btn-secondary" onClick={reprint}>🖨 Reprint Check</button>)}
            <button className="btn btn-secondary" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
            {canEdit && !voided && <button className="btn btn-primary" onClick={save} disabled={busy || !p}>Save</button>}
          </span>
        </div>
      </div>
    </div>
    {printing && <PrintChecksModal canEdit={canEdit} preselect={[{ source: 'bill_payment', id: paymentId }]} onClose={() => setPrinting(false)} onDone={() => { setPrinting(false); load(); }} />}
    </>
  );
}
